import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { savePaymentMethod } from '../services/api/payments';
import { getCustomerAuthUser, saveCustomerAuthUser } from '../services/storage/customerFlow';
import '../styles/route.css';

/**
 * Where the hosted card page sends the customer back.
 *
 * The provider owns the card entry; this route only records the outcome. It
 * mirrors the app's `payments/tokenized` callback, which reads the same
 * parameters — the difference is a normal URL instead of a deep link.
 *
 * Every outcome is named. "Cancelled" and "needs 3D Secure" are ordinary
 * results, not errors, and a customer who backed out of a card form should not
 * be told something went wrong.
 */

type Outcome = 'working' | 'saved' | 'cancelled' | 'requires_action' | 'failed';

function messageFor(outcome: Outcome, providerMessage?: string | null): string {
  if (providerMessage) return providerMessage;

  switch (outcome) {
    case 'saved':
      return 'Your card is saved. You can book a trip now.';
    case 'cancelled':
      return 'No card was saved. You can try again whenever you are ready.';
    case 'requires_action':
      return 'Your bank still needs 3D Secure authentication before this card can be saved.';
    case 'failed':
      return 'We could not save that card. Please retry or use a different one.';
    default:
      return 'Saving your card…';
  }
}

const PaymentReturnPage: React.FC = () => {
  const [params] = useSearchParams();
  const [outcome, setOutcome] = useState<Outcome>('working');

  const status = (params.get('status') ?? '').toLowerCase();
  const providerMessage = params.get('message');

  useEffect(() => {
    let cancelled = false;

    async function record() {
      if (status === 'cancel' || status === 'cancelled') {
        setOutcome('cancelled');
        return;
      }

      if (status === 'requires_action' || status === '3ds_required') {
        setOutcome('requires_action');
        return;
      }

      const externalId = params.get('externalId') ?? params.get('token');
      const user = getCustomerAuthUser() as { id?: string | number } | null;

      /* Without a token there is nothing to persist, whatever the status says. */
      if (!externalId || !user?.id) {
        if (!cancelled) setOutcome('failed');
        return;
      }

      try {
        const updated = await savePaymentMethod(String(user.id), {
          provider: params.get('provider') ?? 'unknown',
          externalId,
          last4: params.get('last4') ?? '',
          brand: params.get('brand') ?? 'Card',
        });

        /* Refresh the stored user so the confirm screen sees the new card
           without a re-login. */
        if (updated && typeof updated === 'object') {
          saveCustomerAuthUser(updated as never);
        }

        if (!cancelled) setOutcome('saved');
      } catch {
        if (!cancelled) setOutcome('failed');
      }
    }

    void record();

    return () => {
      cancelled = true;
    };
  }, [params, status]);

  return (
    <div className="landing-shell">
      <Header />
      <main>
        <section className="sw-section">
          <div className="container">
            <div className="sw-section-head">
              <span className="sw-eyebrow">Payment method</span>
              <h1>{outcome === 'saved' ? 'Card saved' : 'Card setup'}</h1>
            </div>

            <p className={outcome === 'failed' ? 'sw-estimate-error' : 'sw-estimate-note'}>
              {messageFor(outcome, providerMessage)}
            </p>

            <div className="sw-hero-actions">
              <Link className="sw-btn-primary" to="/book/confirm">
                Back to your trip
              </Link>
              <Link className="sw-link-action" to="/account">
                Your deliveries &rarr;
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PaymentReturnPage;
